import React from 'react';
import { Link } from 'react-router-dom';
import { useAtomValue } from 'jotai/utils';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatStreamingAtom, chatMessagesAtom } from 'src/shared/chat';
import { truncateAtWord } from 'src/utils/truncate';
import { ActionCard } from './ActionCard';
import { BatchActionCard } from './BatchActionCard';
import { ChatLink } from './ChatLink';
import { PlanCard } from './PlanCard';
import { WorkingIndicator } from './WorkingIndicator';
import type { ActionCardData, ChatMessage } from './types';
import styles from './AssistantMessage.module.scss';

// Coalesce a flat list of ActionCardData into runs of consecutive same-tool
// entries. Groups of length >= 2 render as a BatchActionCard; groups of
// length 1 render as a regular ActionCard. The natural rhythm
// (search → process → search) stays legible because runs only merge when
// adjacent tools match.
function groupConsecutive(actions: ActionCardData[]): ActionCardData[][] {
  const groups: ActionCardData[][] = [];
  for (const action of actions) {
    const last = groups[groups.length - 1];
    if (last && last[0].tool === action.tool) {
      last.push(action);
    } else {
      groups.push([action]);
    }
  }
  return groups;
}

interface AssistantMessageProps {
  message: ChatMessage;
  isLast?: boolean;
}

// Component overrides for react-markdown. Two overrides preserve the
// existing chat-bubble UX:
//   - `a` → ChatLink (the copy-pill chip used for asset/share URLs).
//   - inline `code` → keeps the existing chip styling; fenced blocks
//     fall through to <pre><code> and pick up the .content pre rules.
// Everything else (headings, lists, blockquote, tables, hr, em, del)
// renders as default HTML and is styled in AssistantMessage.module.scss.
const markdownComponents: Components = {
  a: ({ href, children }) => {
    if (!href) return <>{children}</>;
    const label =
      React.Children.toArray(children)
        .map((c) => (typeof c === 'string' ? c : ''))
        .join('') || href;
    return <ChatLink label={label} url={href} />;
  },
  code: ({ inline, className, children, ...props }) =>
    inline ? (
      <code className={styles.inlineCode} {...props}>
        {children}
      </code>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    ),
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export const AssistantMessage: React.FC<AssistantMessageProps> = ({ message, isLast }) => {
  const isStreaming = useAtomValue(chatStreamingAtom);
  const allMessages = useAtomValue(chatMessagesAtom);
  // Show the indicator for the whole turn, not only before the first output:
  // between tool calls the last card has finished and the next has not
  // started yet, and nothing else on screen says the agent is still working.
  // A spinning card is its own indicator, and a pending plan waits on the user.
  const hasPendingAction = (message.actions || []).some((a) => a.status === 'pending');
  const showWorking =
    isStreaming && isLast && !hasPendingAction && message.plan?.status !== 'pending';

  const precedingUserMsg = React.useMemo(() => {
    const idx = allMessages.findIndex((m) => m.id === message.id);
    if (idx <= 0) return null;
    const prev = allMessages[idx - 1];
    return prev?.role === 'user' ? prev : null;
  }, [allMessages, message.id]);

  const scheduleUrl = precedingUserMsg
    ? `/ai/schedules/new?prompt=${encodeURIComponent(precedingUserMsg.content)}&title=${encodeURIComponent(truncateAtWord(precedingUserMsg.content, 60))}`
    : null;

  const actionGroups = React.useMemo(
    () => groupConsecutive(message.actions || []),
    [message.actions],
  );

  // A pending plan goes last, under the agent's question, so its buttons
  // end the turn. A resolved plan goes first, above the steps it approved.
  const planCard = message.plan && (
    <div className={styles.planContainer}>
      <PlanCard plan={message.plan} />
    </div>
  );
  const planFirst = message.plan && message.plan.status !== 'pending';

  return (
    <div className={styles.wrapper}>
      {planFirst && planCard}

      {actionGroups.length > 0 && (
        <div className={styles.actionsContainer}>
          {actionGroups.map((group) =>
            group.length > 1 ? (
              <BatchActionCard key={group[0].id} group={group} />
            ) : (
              <ActionCard key={group[0].id} action={group[0]} />
            ),
          )}
        </div>
      )}

      {message.content && (
        <div className={styles.content}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {message.content}
          </ReactMarkdown>
        </div>
      )}

      {!planFirst && planCard}

      {showWorking && <WorkingIndicator />}

      {message.content && (
        <div className={styles.meta}>
          <span className={styles.time}>{formatTime(message.timestamp)}</span>
          {scheduleUrl && !isStreaming && (
            <Link to={scheduleUrl} className={styles.scheduleLink} title="Run this on a schedule">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
                <path d="M3 9h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Schedule this
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
